// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/**
 *
 * <HeaderClickable
 *      iconSrc={icon}
 *      onClick={() => !isMobile && history.push(cartUrl ?? "")}
 *      iconProps={{ type: "miniCart" }}
 *      clickableProps={{ "data-test-selector": "cartLink" }}
 *      titleProps={{
 *          "data-test-selector": totalCountDisplay ? "cartLinkQuantity" : "cartLinkSubTotal",
 *      }}
 *      >
 *          <>
 *              {`${translate("Top Message")}`}
 *              <Typography className="sub-title">{`${translate("Bottom Message")}`}</Typography>
 *          </>
 *      </HeaderClickable>
 */

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Clickable, { ClickablePresentationProps, ClickableProps } from "@insite/mobius/Clickable/Clickable";
import Icon, { IconPresentationProps, IconProps, IconWrapper } from "@insite/mobius/Icon";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomAppState } from "@keller/App/Store/Reducers";
import { color, ellipsis, hideElement, xScreen } from "@keller/XDL/Utilities";
import React, { FC, MouseEventHandler } from "react";
import { css } from "styled-components";

interface HeaderClickableProps {
    onClick?: MouseEventHandler<any>;
    iconProps?: IconProps;
    titleProps?: TypographyProps;
    isPopupShown?: boolean;
    numberOfCartItems?: number;
}

type Props = HeaderClickableProps & ClickableProps;

interface HeaderClickableStyle {
    clickable?: ClickablePresentationProps;
    icon?: IconPresentationProps;
    title?: TypographyProps;
    cartItem?: {
        wrapper?: InjectableCss;
        text?: TypographyProps;
    };
}

const styles: HeaderClickableStyle = {
    clickable: {
        css: css`
            padding: 0 5px;

            &:not(.mobile-mode):hover,
            &.popup-shown:not(.mobile-mode) {
                ${IconWrapper}, & ${TypographyStyle} {
                    color: ${color("common.ui1")} !important;
                }
                &:hover {
                    ${IconWrapper}, & ${TypographyStyle} {
                        color: ${color("common.ui1")} !important;
                    }
                }
            }

            &.mobile-mode {
                padding: 10px;
                min-width: 44px;
                height: 44px;
                &.popup-shown {
                    background: ${color("common.ui2")} !important;
                    border-radius: 4px;
                    ${IconWrapper}, & ${TypographyStyle}, div.cart-mobile-mode {
                        color: ${color("common.white")} !important;
                        border-color: ${color("common.white")};
                    }
                }
            }

            &:focus {
                outline: none;
            }
        `,
    },
    icon: {
        size: 28,
        color: "common.ui7",
    },
    title: {
        size: 12,
        weight: "bold",
        color: "primary",
        css: css`
            letter-spacing: 0.5px;
            line-height: 16px;

            display: flex;
            flex-direction: column;
            margin-bottom: 5px;
            max-width: 96px;
            margin-left: 12px;
            & > .sub-title {
                display: block;
                font-weight: normal;
                text-transform: capitalize;
                margin-bottom: 0;

                ${ellipsis(1)}

                &:not(.primary) {
                    color: ${color("text.main")};
                }
            }

            ${xScreen("lte", "md")} {
                ${hideElement}
            }
        `,
        ellipsis: true,
        transform: "uppercase",
    },
    cartItem: {
        wrapper: {
            css: css`
                height: 24px;
                min-width: 24px;
                border: 1px solid ${color("primary", 0.33)};
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
            `,
        },
        text: {
            size: 14,
            weight: "bold",
            color: "primary",
        },
    },
};

const HeaderClickable: FC<Props> = ({
    numberOfCartItems,
    isPopupShown,
    iconProps,
    titleProps,
    children,
    ...otherProps
}) => {
    const isTabletScreen = useAppSelector(state => (state as CustomAppState).xCommon.isTabletScreen);

    const classNames = [isPopupShown ? "popup-shown" : "", isTabletScreen ? "mobile-mode" : ""].join(" ").trim();
    const isCartMobileMode = !!(isTabletScreen && numberOfCartItems && numberOfCartItems > 0);
    return (
        <Clickable {...styles.clickable} {...otherProps} className={classNames}>
            {!isCartMobileMode && <Icon {...styles.icon} {...iconProps} size={isTabletScreen ? 24 : 28} />}
            {isCartMobileMode && (
                <StyledWrapper className="cart-mobile-mode" {...styles.cartItem?.wrapper}>
                    <Typography {...styles.cartItem?.text} {...titleProps}>
                        {numberOfCartItems}
                    </Typography>
                </StyledWrapper>
            )}
            <Typography {...styles.title} {...titleProps}>
                {children}
            </Typography>
        </Clickable>
    );
};

export default HeaderClickable;
